import React, { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useI18n } from "../i18n";
import { useSiteContent } from "../hooks/useSiteContent";

const Contact = () => {
  const { t, lang } = useI18n();
  const { content } = useSiteContent();
  const [form, setForm] = useState({
    name: "", phone: "", email: "", service_type: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const services = [t.services.konut.title, t.services.ticari.title, t.services.fabrika.title, t.services.lojistik.title];

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service_type || !form.message) {
      toast.error(t.contact.fillRequired || "Lütfen tüm zorunlu alanları doldurun.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success(t.contact.success);
      setForm({ name: "", phone: "", email: "", service_type: "", message: "" });
      setTimeout(() => setDone(false), 5000);
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  const rawOffices = Array.isArray(content["contact.offices"]) && content["contact.offices"].length > 0
    ? content["contact.offices"]
    : [
        {
          key: "duzce",
          title: "Merkez Ofis · Düzce",
          address: "Düzce, Türkiye",
          phones: ["0532 630 38 39"],
          emails: ["info@demiray.net.tr", "tahsin@demiray.net.tr"],
        },
      ];
  const offices = lang === "tr"
    ? rawOffices
    : rawOffices.map((o) => ({ ...o, title: (t.contact.officeTitles && t.contact.officeTitles[o.key]) || o.title }));

  const contactTitle = (lang === "tr" && content["contact.title"]) || t.contact.title;
  const contactSubtitle = (lang === "tr" && content["contact.subtitle"]) || t.contact.subtitle;

  return (
    <section
      id="contact"
      className="relative py-24 md:py-32 bg-[var(--surface)] overflow-hidden"
      data-testid="contact-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mb-12 md:mb-16"
        >
          <div className="overline mb-4">{t.contact.overline}</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-white leading-[1.05] mb-6">
            {contactTitle}
          </h2>
          <p className="text-base text-[var(--text-muted)] leading-relaxed">{contactSubtitle}</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Offices */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 space-y-6"
          >
            <div className="overline">{t.contact.offices}</div>
            {offices.map((o) => (
              <div key={o.key} className="border-l-2 border-[var(--border)] pl-5 py-2 hover:border-white transition-colors" data-testid={`office-${o.key}`}>
                <div className="font-display text-base font-medium text-white mb-3">{o.title}</div>
                {o.address && (
                  <div className="flex items-start gap-2 text-sm text-[var(--text-main)] mb-2">
                    <MapPin size={14} className="mt-0.5 shrink-0 text-[var(--text-muted)]" />
                    <span className="leading-relaxed">{o.address}</span>
                  </div>
                )}
                {(o.phones || []).map((p) => (
                  <a key={p} href={`tel:${p.replace(/\s/g, "")}`} className="flex items-center gap-2 text-sm text-[var(--text-main)] hover:text-white mb-1">
                    <Phone size={13} className="text-[var(--text-muted)]" /> {p}
                  </a>
                ))}
                {(o.emails || []).map((e) => (
                  <a key={e} href={`mailto:${e}`} className="flex items-center gap-2 text-sm text-[var(--text-main)] hover:text-white mb-1">
                    <Mail size={13} className="text-[var(--text-muted)]" /> {e}
                  </a>
                ))}
              </div>
            ))}
          </motion.div>

          {/* Form */}
          <motion.form
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-7 border border-[var(--border)] p-8 md:p-12 bg-[var(--bg)] space-y-5"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-xs tracking-widest uppercase text-[var(--text-muted)] mb-2 block">{t.contact.name} *</label>
                <input name="name" value={form.name} onChange={onChange} className="input-sharp" required data-testid="contact-input-name" />
              </div>
              <div>
                <label className="text-xs tracking-widest uppercase text-[var(--text-muted)] mb-2 block">{t.contact.phone} *</label>
                <input name="phone" value={form.phone} onChange={onChange} className="input-sharp" required data-testid="contact-input-phone" />
              </div>
            </div>

            <div>
              <label className="text-xs tracking-widest uppercase text-[var(--text-muted)] mb-2 block">{t.contact.email}</label>
              <input type="email" name="email" value={form.email} onChange={onChange} className="input-sharp" data-testid="contact-input-email" />
            </div>

            <div>
              <label className="text-xs tracking-widest uppercase text-[var(--text-muted)] mb-2 block">{t.contact.service} *</label>
              <select name="service_type" value={form.service_type} onChange={onChange} className="input-sharp appearance-none cursor-pointer" required data-testid="contact-select-service">
                <option value="">{t.contact.selectService}</option>
                {services.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div>
              <label className="text-xs tracking-widest uppercase text-[var(--text-muted)] mb-2 block">{t.contact.message} *</label>
              <textarea name="message" value={form.message} onChange={onChange} rows={5} className="input-sharp resize-none" required data-testid="contact-input-message" />
            </div>

            <button type="submit" disabled={submitting} className="btn-sharp btn-primary w-full md:w-auto disabled:opacity-60" data-testid="contact-submit-button">
              {submitting ? <><Loader2 size={16} className="animate-spin" />{t.contact.sending}</>
                : done ? <><CheckCircle2 size={16} />{t.contact.success.split(".")[0]}</>
                : <><Send size={16} />{t.contact.send}</>}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
